import { Component } from '@theme/component';

/** @extends {Component<{slides: HTMLElement[], dots?: HTMLButtonElement[], controls?: HTMLElement, caption?: HTMLElement, pause?: HTMLButtonElement, announcement?: HTMLElement}>} */
export class LussommoSandSlideshow extends Component {
  #controller;
  #timer;
  #media;
  #intersection;
  #inView = true;
  #index = 0;
  #paused = false;
  #hovered = false;
  #focused = false;
  #pointer;
  #remaining = 0;
  #startedAt = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#intersection?.disconnect();
    this.#intersection = undefined;
    clearTimeout(this.#timer);
  }

  #initialize() {
    this.#controller?.abort();
    this.#intersection?.disconnect();
    this.#intersection = undefined;
    clearTimeout(this.#timer);
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#media = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#hovered = false;
    this.#focused = this.contains(document.activeElement);
    this.#pointer = undefined;
    this.#inView = true;
    if (window.IntersectionObserver) {
      const intersection = new window.IntersectionObserver((entries) => {
        if (this.#intersection !== intersection) return;
        const entry = entries.find((entry) => entry.target === this);
        if (!entry) return;
        this.#inView = entry.isIntersecting;
        this.#schedule();
      });
      this.#intersection = intersection;
      intersection.observe(this);
    }
    this.#media.addEventListener('change', () => this.#schedule(), { signal });
    document.addEventListener('visibilitychange', () => this.#schedule(), { signal });
    this.addEventListener('mouseenter', () => { this.#hovered = true; this.#schedule(); }, { signal });
    this.addEventListener('mouseleave', () => { this.#hovered = false; this.#schedule(); }, { signal });
    this.addEventListener('focusin', () => { this.#focused = true; this.#schedule(); }, { signal });
    this.addEventListener('focusout', (event) => { this.#focused = this.contains(event.relatedTarget); this.#schedule(); }, { signal });
    this.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.target instanceof Element && event.target.closest('button, a')) return;
      this.#pointer = { x: event.clientX, y: event.clientY, id: event.pointerId };
    }, { signal });
    this.addEventListener('pointerup', (event) => {
      const start = this.#pointer;
      this.#pointer = undefined;
      if (!start || start.id !== event.pointerId) return;
      const x = event.clientX - start.x;
      const y = event.clientY - start.y;
      if (Math.abs(x) < 45 || Math.abs(x) <= Math.abs(y)) return;
      const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      this.#go(this.#index + (x < 0 ? 1 : -1) * direction, true);
    }, { signal });
    this.addEventListener('pointercancel', () => { this.#pointer = undefined; }, { signal });
    this.addEventListener('shopify:block:select', (event) => {
      const selected = this.refs.slides?.findIndex((slide) => slide === event.target || slide.contains(event.target));
      if (selected >= 0) { this.#paused = true; this.#go(selected, true); }
    }, { signal });
    if (this.refs.controls) this.refs.controls.hidden = (this.refs.slides?.length ?? 0) < 2;
    this.#go(this.#index, false);
  }

  /** Go to the previous photo. */
  previous() { this.#go(this.#index - 1, true); }

  /** Go to the next photo. */
  next() { this.#go(this.#index + 1, true); }

  /** @param {Event} event - A dot button click. */
  selectSlide(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-index]');
    if (button) this.#go(Number(button.dataset.index), true);
  }

  /** Toggle the visitor's explicit playback preference. */
  togglePlayback() {
    this.#paused = !this.#paused;
    this.#schedule();
  }

  #go(index, announce) {
    const slides = this.refs.slides ?? [];
    if (!slides.length || !Number.isFinite(index)) return;
    this.#remaining = Math.max(3000, Number(this.dataset.interval) || 6000);
    this.#startedAt = 0;
    this.#index = ((index % slides.length) + slides.length) % slides.length;
    for (const [position, slide] of slides.entries()) {
      const active = position === this.#index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      const dot = this.refs.dots?.[position];
      if (active) dot?.setAttribute('aria-current', 'true');
      else dot?.removeAttribute('aria-current');
    }
    const slide = slides[this.#index];
    if (this.refs.caption) this.refs.caption.textContent = slide.dataset.caption ?? '';
    if (announce && this.refs.announcement) this.refs.announcement.textContent = `${slide.getAttribute('aria-label')}. ${slide.dataset.caption ?? ''}`;
    this.#schedule();
  }

  #schedule() {
    clearTimeout(this.#timer);
    if (this.#startedAt) this.#remaining = Math.max(0, this.#remaining - (Date.now() - this.#startedAt));
    this.#startedAt = 0;
    const disabled = this.dataset.autoplay !== 'true' || this.#media?.matches || window.Shopify?.designMode;
    const stopped = disabled || this.#paused;
    const pause = this.refs.pause;
    if (pause) {
      pause.hidden = disabled;
      pause.setAttribute('aria-label', stopped ? pause.dataset.playLabel : pause.dataset.pauseLabel);
      pause.dataset.playback = stopped ? 'stopped' : 'playing';
    }
    this.classList.toggle('is-paused', stopped || this.#hovered || this.#focused || document.hidden || !this.#inView);
    if (stopped || this.#hovered || this.#focused || document.hidden || !this.#inView || (this.refs.slides?.length ?? 0) < 2) return;
    this.#startedAt = Date.now();
    this.#timer = setTimeout(() => this.#go(this.#index + 1, false), this.#remaining);
  }
}

/** @extends {Component<{cards?: HTMLElement[], filters?: HTMLElement, count?: HTMLElement, empty?: HTMLElement}>} */
export class LussommoSandFilter extends Component {
  #family = 'all';

  connectedCallback() {
    super.connectedCallback();
    this.#update();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#update();
  }

  /** @param {Event} event - A colour-family button click. */
  filter(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-family]');
    if (!(button instanceof HTMLButtonElement) || !this.refs.filters?.contains(button)) return;
    this.#family = button.dataset.family;
    this.#update();
  }

  #update() {
    const cards = this.refs.cards ?? [];
    const filters = this.refs.filters;
    if (filters) {
      filters.hidden = !cards.some((card) => card.dataset.family !== 'all');
      const buttons = filters.querySelectorAll('button[data-family]');
      for (const button of buttons) {
        const family = button.dataset.family;
        button.hidden = family !== 'all' && !cards.some((card) => card.dataset.family === family);
        button.setAttribute('aria-pressed', String(family === this.#family));
      }
      if (![...buttons].some((button) => button.dataset.family === this.#family && !button.hidden)) {
        this.#family = 'all';
        for (const button of buttons) button.setAttribute('aria-pressed', String(button.dataset.family === 'all'));
      }
    }
    let count = 0;
    for (const card of cards) {
      card.hidden = this.#family !== 'all' && card.dataset.family !== this.#family;
      if (!card.hidden) count++;
    }
    if (this.refs.count) this.refs.count.textContent = (count === 1 ? this.dataset.countOne : this.dataset.countMany)?.replace('[count]', String(count)) ?? String(count);
    if (this.refs.empty) this.refs.empty.hidden = count !== 0;
  }
}

if (!customElements.get('lussommo-sand-slideshow')) customElements.define('lussommo-sand-slideshow', LussommoSandSlideshow);
if (!customElements.get('lussommo-sand-filter')) customElements.define('lussommo-sand-filter', LussommoSandFilter);
